/**
 * Caliche Routes Component
 * Crowdsourced route photos for field navigation
 * Shows map with route photo pins, allows adding new photos with GPS
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import {
  Dialog, DialogContent, Box, Typography, IconButton,
  Button, TextField, Select, MenuItem, FormControl, InputLabel,
  Chip, Tooltip, Snackbar, Alert, Card,
  Stepper, Step, StepLabel, StepContent, Tabs, Tab, Badge
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import ThumbDownIcon from '@mui/icons-material/ThumbDown';
import ThumbUpOutlinedIcon from '@mui/icons-material/ThumbUpOutlined';
import ThumbDownOutlinedIcon from '@mui/icons-material/ThumbDownOutlined';
import ForkRightIcon from '@mui/icons-material/ForkRight';
import PublicIcon from '@mui/icons-material/Public';
import PersonIcon from '@mui/icons-material/Person';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import FlipCameraAndroidIcon from '@mui/icons-material/FlipCameraAndroid';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import MapIcon from '@mui/icons-material/Map';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import DirectionsIcon from '@mui/icons-material/Directions';
import ShareIcon from '@mui/icons-material/Share';
import GoogleIcon from '@mui/icons-material/Google';
// Apple Maps and Waze removed - only Google Maps supports waypoints
import ZiaCompassIcon from './icons/ZiaCompassIcon';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import LayersIcon from '@mui/icons-material/Layers';
import RouteIcon from '@mui/icons-material/Route';
import ArchiveIcon from '@mui/icons-material/Archive';
import PublishIcon from '@mui/icons-material/Publish';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { useAppStore } from '../stores/appStore';
import { getCyberColors } from '../theme/cyberColors';
import { CyberSpinner } from './CyberSpinner';
import { telegram } from '../services/telegram';
import { compressImageForUpload, createThumbnail, isSupportedImageType } from '../services/exif';
import { requestPreciseLocation } from '../services/highPrecisionLocation';

// Detect if running on Android
const isAndroid = /android/i.test(navigator.userAgent);

interface RoutePhoto {
  id: number;
  latitude: number;
  longitude: number;
  step_number: number;
  landmark_type: string;
  description: string;
  facility_id: string | null;
  facility_name: string | null;
  api_number: string | null;
  photo_url: string;
  photo_thumbnail_url: string | null;
  status: string;
  created_at: string;
  // Voting & social fields
  upvotes: number;
  downvotes: number;
  score: number;
  stars: number;
  forks: number;
  submitted_by: string | null;
  submitted_by_username: string | null;
  // User's interaction state
  userVoted?: 'up' | 'down' | null;
  userStarred?: boolean;
}

// Trust level based on score
const getTrustLevel = (score: number): { label: string; color: string; bgColor: string; icon: string } => {
  if (score >= 10) return { label: 'Highly Trusted', color: '#22c55e', bgColor: 'rgba(34, 197, 94, 0.15)', icon: '🏆' };
  if (score >= 5) return { label: 'Trusted', color: '#4ade80', bgColor: 'rgba(74, 222, 128, 0.15)', icon: '✅' };
  if (score >= 1) return { label: 'Good', color: '#a3e635', bgColor: 'rgba(163, 230, 53, 0.15)', icon: '👍' };
  if (score === 0) return { label: 'New', color: '#94a3b8', bgColor: 'rgba(148, 163, 184, 0.15)', icon: '🆕' };
  if (score >= -2) return { label: 'Mixed', color: '#fbbf24', bgColor: 'rgba(251, 191, 36, 0.15)', icon: '⚠️' };
  return { label: 'Avoid', color: '#ef4444', bgColor: 'rgba(239, 68, 68, 0.15)', icon: '🚫' };
};

// Get marker color based on score
const getMarkerColor = (score: number): string => {
  if (score >= 5) return '#22c55e'; // Green - trusted
  if (score >= 1) return '#4ade80'; // Light green - good
  if (score === 0) return '#60a5fa'; // Blue - new
  if (score >= -2) return '#fbbf24'; // Yellow - mixed
  return '#ef4444'; // Red - avoid
};

interface CommunityRoutesProps {
  open: boolean;
  onClose: () => void;
  /** Pre-fill location from right-click on map */
  initialLocation?: { lat: number; lng: number } | null;
  /** Open directly in add photo mode */
  startInAddMode?: boolean;
}

const LANDMARK_TYPES = [
  { value: 'turnoff', label: 'Turnoff/Intersection', icon: '🔀' },
  { value: 'gate', label: 'Gate', icon: '🚪' },
  { value: 'cattle_guard', label: 'Cattle Guard', icon: '🐄' },
  { value: 'sign', label: 'Sign/Marker', icon: '🪧' },
  { value: 'landmark', label: 'Landmark (barn, windmill, tank)', icon: '🏠' },
  { value: 'hazard', label: 'Hazard (washout, low clearance)', icon: '⚠️' },
  { value: 'destination', label: 'Destination/Facility', icon: '🏁' },
  { value: 'other', label: 'Other', icon: '📍' }
];

export function CommunityRoutes({ open, onClose, initialLocation, startInAddMode }: CommunityRoutesProps) {
  const darkMode = useAppStore((s) => s.darkMode);
  const highContrast = useAppStore((s) => s.highContrast);
  const cyber = getCyberColors(darkMode, highContrast);

  // Map state
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<google.maps.Map | null>(null);
  const markersRef = useRef<google.maps.Marker[]>([]);
  
  // Photo data
  const [photos, setPhotos] = useState<RoutePhoto[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedPhoto, setSelectedPhoto] = useState<RoutePhoto | null>(null);
  
  // Add photo mode
  const [showAddPhoto, setShowAddPhoto] = useState(false);
  const [addPhotoStep, setAddPhotoStep] = useState(0);
  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [extractedGPS, setExtractedGPS] = useState<{ lat: number; lng: number } | null>(null);
  const [manualLocation, setManualLocation] = useState<{ lat: number; lng: number } | null>(null);
  const [stepNumber, setStepNumber] = useState(1);
  const [landmarkType, setLandmarkType] = useState('turnoff');
  const [description, setDescription] = useState('');
  const [apiNumber, setApiNumber] = useState('');
  const [submitting, setSubmitting] = useState(false);
  
  // Navigate route mode
  const [navigateMode, setNavigateMode] = useState(false);
  const [routePhotos, setRoutePhotos] = useState<RoutePhoto[]>([]);
  const [currentRouteIndex, setCurrentRouteIndex] = useState(0);
  
  // Toast
  const [toast, setToast] = useState<{ open: boolean; message: string; severity: 'success' | 'error' | 'info' }>({
    open: false, message: '', severity: 'info'
  });

  // View mode: 'landmarks' = individual photos, 'routes' = waypoint-based routes
  type ViewMode = 'landmarks' | 'routes';
  const [viewMode] = useState<ViewMode>('routes');
  
  // Filter tabs: 'all' = all public photos, 'mine' = my posts
  type FilterTab = 'all' | 'mine';
  const [activeFilter, setActiveFilter] = useState<FilterTab>('all');
  const [myPostsCount, setMyPostsCount] = useState(0);
  
  // Routes tab: 'my' = user's routes, 'saved' = starred routes, 'public' = community routes
  type RoutesTab = 'my' | 'saved' | 'public';
  const [routesTab, setRoutesTab] = useState<RoutesTab>('my');
  const [routes, setRoutes] = useState<any[]>([]);
  const [routesLoading, setRoutesLoading] = useState(false);
  const [myRoutesCount, setMyRoutesCount] = useState(0);
  const [savedRoutesCount, setSavedRoutesCount] = useState(0);
  const [publicRoutesCount, setPublicRoutesCount] = useState(0);
  const [selectedRoute, setSelectedRoute] = useState<any | null>(null);
  // TODO: Implement route detail view when selectedRoute is set
  void selectedRoute; // Silence unused warning - will be used for route detail modal
  
  // Starred route photos (synced via Telegram CloudStorage) - for landmarks view
  const [starredRouteIds, setStarredRouteIds] = useState<Set<number>>(new Set());
  // Starred routes (for routes view) - keyed by route_id string
  const [starredRouteRouteIds, setStarredRouteRouteIds] = useState<Set<string>>(new Set());
  const [votingPhotoId, setVotingPhotoId] = useState<number | null>(null);

  // Share dialog state
  const [showShareDialog, setShowShareDialog] = useState(false);
  const [lastSubmittedPhotoId, setLastSubmittedPhotoId] = useState<number | null>(null);
  const [lastSubmittedLocation, setLastSubmittedLocation] = useState<{ lat: number; lng: number; landmarkType: string; description: string } | null>(null);

  // Native camera state (for Android)
  const [showNativeCamera, setShowNativeCamera] = useState(false);
  const [cameraStream, setCameraStream] = useState<MediaStream | null>(null);
  const [cameraReady, setCameraReady] = useState(false);
  const [cameraError, setCameraError] = useState<string | null>(null);
  const [facingMode, setFacingMode] = useState<'environment' | 'user'>('environment');
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  const cameraInputRef = useRef<HTMLInputElement>(null);
  const galleryInputRef = useRef<HTMLInputElement>(null);

  // Start native camera (for Android)
  const startNativeCamera = useCallback(async () => {
    console.log('📷 Starting native camera...');
    setCameraError(null);
    setCameraReady(false);
    setShowNativeCamera(true); // Show dialog first
    
    try {
      // Stop any existing stream
      if (cameraStream) {
        cameraStream.getTracks().forEach(track => track.stop());
      }

      // Request camera permission with simpler constraints for Android compatibility
      const constraints: MediaStreamConstraints = {
        video: {
          facingMode: { ideal: facingMode },
          width: { ideal: 1280 },
          height: { ideal: 720 }
        },
        audio: false
      };

      console.log('📷 Requesting camera with constraints:', constraints);
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      console.log('📷 Got camera stream:', stream.getVideoTracks()[0]?.label);
      
      setCameraStream(stream);
      
      // Wait a tick for dialog to render, then attach stream
      setTimeout(() => {
        if (videoRef.current) {
          console.log('📷 Attaching stream to video element');
          videoRef.current.srcObject = stream;
          videoRef.current.onloadedmetadata = () => {
            console.log('📷 Video metadata loaded, playing...');
            videoRef.current?.play()
              .then(() => {
                console.log('📷 Video playing successfully');
                setCameraReady(true);
              })
              .catch(err => {
                console.error('📷 Video play error:', err);
                setCameraError('Could not start camera preview');
              });
          };
        }
      }, 100);
      
    } catch (error: any) {
      console.error('📷 Camera access error:', error);
      setCameraError(error.message || 'Camera access denied');
      
      // Show alert and fallback
      telegram.showAlert(`Camera error: ${error.message || 'Access denied'}. Try using "Choose from Gallery" instead.`);
      
      // Close camera dialog and fallback to file input
      setTimeout(() => {
        setShowNativeCamera(false);
        cameraInputRef.current?.click();
      }, 500);
    }
  }, [cameraStream, facingMode]);

  // Stop native camera
  const stopNativeCamera = useCallback(() => {
    console.log('📷 Stopping native camera');
    if (cameraStream) {
      cameraStream.getTracks().forEach(track => {
        track.stop();
        console.log('📷 Stopped track:', track.label);
      });
      setCameraStream(null);
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    setCameraReady(false);
    setShowNativeCamera(false);
  }, [cameraStream]);

  // Capture photo from native camera
  const captureFromNativeCamera = useCallback(() => {
    if (!videoRef.current || !canvasRef.current) {
      console.error('📷 Video or canvas ref not available');
      return;
    }

    const video = videoRef.current;
    const canvas = canvasRef.current;
    
    console.log('📷 Capturing photo, video dimensions:', video.videoWidth, 'x', video.videoHeight);
    
    // Ensure video has dimensions
    if (video.videoWidth === 0 || video.videoHeight === 0) {
      telegram.showAlert('Camera not ready. Please wait a moment and try again.');
      return;
    }
    
    // Set canvas size to video dimensions
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    
    // Draw video frame to canvas
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    
    ctx.drawImage(video, 0, 0);
    
    // Convert to blob
    canvas.toBlob(async (blob) => {
      if (!blob) {
        console.error('📷 Failed to create blob from canvas');
        return;
      }
      
      console.log('📷 Photo captured, blob size:', blob.size);
      
      // Create file from blob
      const file = new File([blob], `photo_${Date.now()}.jpg`, { type: 'image/jpeg' });
      
      // Stop camera
      stopNativeCamera();
      
      // Process the file (same as handleFileSelect)
      setPhotoFile(file);
      
      // Create preview
      const reader = new FileReader();
      reader.onload = () => setPhotoPreview(reader.result as string);
      reader.readAsDataURL(file);
      
      // No GPS from native camera, will need manual location
      setExtractedGPS(null);
      setAddPhotoStep(1);
      
      telegram.hapticFeedback('success');
    }, 'image/jpeg', 0.85);
  }, [stopNativeCamera]);

  // Switch camera (front/back)
  const switchCamera = useCallback(async () => {
    const newFacing = facingMode === 'environment' ? 'user' : 'environment';
    console.log('📷 Switching camera to:', newFacing);
    setFacingMode(newFacing);
    setCameraReady(false);
    
    // Restart camera with new facing mode
    if (cameraStream) {
      cameraStream.getTracks().forEach(track => track.stop());
    }
    
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: newFacing }, width: { ideal: 1280 }, height: { ideal: 720 } },
        audio: false
      });
      setCameraStream(stream);
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.onloadedmetadata = () => {
          videoRef.current?.play()
            .then(() => setCameraReady(true))
            .catch(err => console.error('Play error:', err));
        };
      }
    } catch (error) {
      console.error('📷 Camera switch error:', error);
      telegram.showAlert('Could not switch camera');
    }
  }, [facingMode, cameraStream]);

  // Handle camera button click - use native camera on Android
  const handleCameraClick = useCallback(() => {
    if (isAndroid) {
      // Use native camera for Android
      startNativeCamera();
    } else {
      // Use file input with capture for iOS (works great)
      cameraInputRef.current?.click();
    }
  }, [startNativeCamera]);

  // Get shared route photo ID from store
  const sharedRoutePhotoId = useAppStore((s) => s.sharedRoutePhotoId);
  const setSharedRoutePhotoId = useAppStore((s) => s.setSharedRoutePhotoId);

  // Load starred route photos from Telegram CloudStorage (for landmarks view)
  const loadStarredRoutes = useCallback(async () => {
    try {
      const stored = await telegram.cloudStorageGet('starred_routes');
      if (stored) {
        const ids = JSON.parse(stored) as number[];
        setStarredRouteIds(new Set(ids));
      }
    } catch (error) {
      console.error('Failed to load starred routes:', error);
    }
  }, []);

  // Save starred route photos to Telegram CloudStorage
  const saveStarredRoutes = useCallback(async (ids: Set<number>) => {
    try {
      await telegram.cloudStorageSet('starred_routes', JSON.stringify([...ids]));
    } catch (error) {
      console.error('Failed to save starred routes:', error);
    }
  }, []);

  // Load starred routes (for routes view) from Telegram CloudStorage
  const loadStarredRouteRoutes = useCallback(async () => {
    try {
      const stored = await telegram.cloudStorageGet('starred_route_routes');
      if (stored) {
        const ids = JSON.parse(stored) as string[];
        setStarredRouteRouteIds(new Set(ids));
        setSavedRoutesCount(ids.length);
      }
    } catch (error) {
      console.error('Failed to load starred route routes:', error);
    }
  }, []);

  // Save starred routes to Telegram CloudStorage
  const saveStarredRouteRoutes = useCallback(async (ids: Set<string>) => {
    try {
      await telegram.cloudStorageSet('starred_route_routes', JSON.stringify([...ids]));
      setSavedRoutesCount(ids.size);
    } catch (error) {
      console.error('Failed to save starred route routes:', error);
    }
  }, []);

  // Toggle star on a route (routes tab)
  const handleStarRoute = useCallback(async (route: any) => {
    telegram.hapticFeedback('light');
    const routeKey = route.route_id || `${route.routeType}-${route.id}`;
    const newStarred = new Set(starredRouteRouteIds);
    
    if (newStarred.has(routeKey)) {
      newStarred.delete(routeKey);
      setToast({ open: true, message: '⭐ Removed from saved routes', severity: 'info' });
    } else {
      newStarred.add(routeKey);
      // Also store the route data for offline access
      try {
        const savedRoutes = await telegram.cloudStorageGet('saved_routes_data') || '{}';
        const routesData = JSON.parse(savedRoutes);
        routesData[routeKey] = route;
        await telegram.cloudStorageSet('saved_routes_data', JSON.stringify(routesData));
      } catch (e) {
        console.error('Failed to store route data:', e);
      }
      setToast({ open: true, message: '⭐ Added to saved routes!', severity: 'success' });
    }
    
    setStarredRouteRouteIds(newStarred);
    await saveStarredRouteRoutes(newStarred);
    
    // Update routes list to reflect star state
    setRoutes(prev => prev.map(r => {
      const key = r.route_id || `${r.routeType}-${r.id}`;
      return key === routeKey ? { ...r, userStarred: newStarred.has(routeKey) } : r;
    }));
  }, [starredRouteRouteIds, saveStarredRouteRoutes]);

  // Load all route counts for tab badges (fetches counts without loading full data)
  const loadAllRouteCounts = async () => {
    const userId = telegram.getUserId();
    const headers: Record<string, string> = {
      'Content-Type': 'application/json'
    };
    if (userId) {
      headers['X-Telegram-User-Id'] = userId;
      headers['X-Telegram-Username'] = telegram.getUserName() || '';
    }

    try {
      // Fetch all route types to count for all tabs
      const [myV2Response, publicV2Response, landmarkRoutesResponse] = await Promise.all([
        fetch(`/api/routes-v2?tab=my`, { headers }),
        fetch(`/api/routes-v2?tab=public`, { headers }),
        fetch('/api/landmark-routes/all', { headers })
      ]);
      
      const myV2Data = await myV2Response.json();
      const publicV2Data = await publicV2Response.json();
      const landmarkRoutesData = await landmarkRoutesResponse.json();
      
      const myV2Routes = myV2Data.routes || [];
      const publicV2Routes = publicV2Data.routes || [];
      const landmarkRoutes = landmarkRoutesData.routes || [];
      
      // Count my routes (user's own) - also count anonymous when not authenticated
      const myLandmarkCount = landmarkRoutes.filter((r: any) => {
        if (!userId) {
          return r.created_by_user_id === 'anonymous';
        }
        return r.created_by_user_id === userId;
      }).length;
      setMyRoutesCount(myV2Routes.length + myLandmarkCount);
      
      // Count public routes (public and not user's own)
      const publicLandmarkCount = landmarkRoutes.filter((r: any) => {
        if (!userId) {
          return r.is_public && r.created_by_user_id !== 'anonymous';
        }
        return r.is_public && r.created_by_user_id !== userId;
      }).length;
      setPublicRoutesCount(publicV2Routes.length + publicLandmarkCount);
      
      console.log('[CommunityRoutes] Loaded route counts:', { 
        my: myV2Routes.length + myLandmarkCount, 
        public: publicV2Routes.length + publicLandmarkCount 
      });
    } catch (error) {
      console.error('Failed to load route counts:', error);
    }
  };

  // Load starred routes and photos on open
  useEffect(() => {
    if (open) {
      loadStarredRoutes().then(() => {
        loadStarredRouteRoutes().then(() => {
          loadPhotos();
          // Also load routes and counts for routes view
          loadRoutes('my');
          loadAllRouteCounts();
        });
      });
    }
  }, [open, loadStarredRoutes]);

  // Handle initialLocation and startInAddMode from right-click
  useEffect(() => {
    if (open && startInAddMode && initialLocation) {
      // Pre-fill location and open add photo mode
      setManualLocation(initialLocation);
      setExtractedGPS(null);
      setShowAddPhoto(true);
      setAddPhotoStep(0); // Start at photo selection step
      // Center map on the location
      if (mapInstanceRef.current) {
        mapInstanceRef.current.setCenter(initialLocation);
        mapInstanceRef.current.setZoom(15);
      }
    }
  }, [open, startInAddMode, initialLocation]);

  // Handle shared route - focus on the shared photo when it loads
  useEffect(() => {
    const handleSharedRoute = async () => {
      if (!open || !sharedRoutePhotoId) return;
      
      // First check if photo is already in the list
      let sharedPhoto = photos.find(p => p.id === sharedRoutePhotoId);
      
      // If not found in list (maybe pending or not loaded), fetch it directly
      if (!sharedPhoto) {
        try {
          const response = await fetch(`/api/routes?id=${sharedRoutePhotoId}`);
          const data = await response.json();
          if (data.success && data.photo) {
            sharedPhoto = data.photo;
            // Add to photos list if not already there
            setPhotos(prev => {
              if (prev.some(p => p.id === sharedPhoto!.id)) return prev;
              return [...prev, sharedPhoto!];
            });
          }
        } catch (error) {
          console.error('Failed to fetch shared photo:', error);
          setToast({ open: true, message: 'Could not find shared location', severity: 'error' });
          setSharedRoutePhotoId(null);
          return;
        }
      }
      
      if (sharedPhoto) {
        setSelectedPhoto(sharedPhoto);
        // Center map on shared photo
        if (mapInstanceRef.current) {
          mapInstanceRef.current.setCenter({ lat: sharedPhoto.latitude, lng: sharedPhoto.longitude });
          mapInstanceRef.current.setZoom(14);
        }
        // Clear the shared route ID so it doesn't re-focus on every render
        setSharedRoutePhotoId(null);
      }
    };
    
    handleSharedRoute();
  }, [open, sharedRoutePhotoId, photos, setSharedRoutePhotoId]);

  // Initialize map
  useEffect(() => {
    if (open && mapRef.current && !mapInstanceRef.current && window.google) {
      const map = new google.maps.Map(mapRef.current, {
        center: { lat: 32.5, lng: -104.5 }, // SE New Mexico
        zoom: 8,
        mapTypeId: 'satellite',
        gestureHandling: 'greedy',
        disableDefaultUI: true,
        zoomControl: false,
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: false,
        styles: darkMode ? [
          { elementType: 'geometry', stylers: [{ color: '#1d2c4d' }] },
          { elementType: 'labels.text.fill', stylers: [{ color: '#8ec3b9' }] },
          { elementType: 'labels.text.stroke', stylers: [{ color: '#1a3646' }] }
        ] : []
      });
      
      mapInstanceRef.current = map;
      
      // Click to set manual location when adding photo
      map.addListener('click', (e: google.maps.MapMouseEvent) => {
        if (showAddPhoto && !extractedGPS && e.latLng) {
          setManualLocation({ lat: e.latLng.lat(), lng: e.latLng.lng() });
        }
      });
    }
    
    return () => {
      if (!open) {
        mapInstanceRef.current = null;
      }
    };
  }, [open, darkMode, showAddPhoto, extractedGPS]);

  // Update markers when photos change
  useEffect(() => {
    if (!mapInstanceRef.current) return;
    
    // Clear existing markers
    markersRef.current.forEach(m => m.setMap(null));
    markersRef.current = [];
    
    // Add markers for each photo
    photos.forEach(photo => {
      const landmark = LANDMARK_TYPES.find(l => l.value === photo.landmark_type);
      const markerColor = getMarkerColor(photo.score || 0);
      const trust = getTrustLevel(photo.score || 0);
      
      const marker = new google.maps.Marker({
        position: { lat: photo.latitude, lng: photo.longitude },
        map: mapInstanceRef.current,
        title: `${landmark?.label || 'Route Photo'} - ${trust.label} (${photo.score >= 0 ? '+' : ''}${photo.score || 0})`,
        label: {
          text: landmark?.icon || '📍',
          fontSize: '18px'
        },
        icon: {
          path: google.maps.SymbolPath.CIRCLE,
          scale: 18,
          fillColor: markerColor,
          fillOpacity: 0.95,
          strokeColor: '#ffffff',
          strokeWeight: 2
        }
      });
      
      marker.addListener('click', () => {
        telegram.hapticFeedback('light');
        setSelectedPhoto(photo);
      });
      
      markersRef.current.push(marker);
    });
  }, [photos, darkMode]);

  const loadPhotos = async (filter: FilterTab = activeFilter) => {
    setLoading(true);
    try {
      const userId = telegram.getUserId();
      let url = '/api/routes?';
      
      if (filter === 'mine' && userId) {
        url += `user_id=${userId}`;
      } else {
        url += 'status=approved';
      }
      
      // Add user_id for vote state if available
      if (userId) {
        url += `&viewer_id=${userId}`;
      }
      
      const response = await fetch(url);
      const data = await response.json();
      if (data.success) {
        // Mark starred photos
        const photosWithStars = data.photos.map((p: RoutePhoto) => ({
          ...p,
          userStarred: starredRouteIds.has(p.id)
        }));
        setPhotos(photosWithStars);
      }
      
      // Load my posts count
      if (userId) {
        loadMyPostsCount(userId);
      }
    } catch (error) {
      console.error('Failed to load route photos:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadMyPostsCount = async (userId: string) => {
    try {
      const response = await fetch(`/api/routes/my-counts?user_id=${userId}`);
      const data = await response.json();
      if (data.success) {
        setMyPostsCount(data.total || 0);
      }
    } catch (error) {
      console.error('Failed to load my posts count:', error);
    }
  };

  const handleFilterChange = (_: React.SyntheticEvent, newValue: FilterTab) => {
    setActiveFilter(newValue);
    loadPhotos(newValue);
    telegram.hapticFeedback('light');
  };

  // Routes V2: Load waypoint-based routes (including landmark routes)
  const loadRoutes = async (tab: RoutesTab = routesTab) => {
    setRoutesLoading(true);
    try {
      const userId = telegram.getUserId();
      const headers: Record<string, string> = {
        'Content-Type': 'application/json'
      };
      if (userId) {
        headers['X-Telegram-User-Id'] = userId;
        headers['X-Telegram-Username'] = telegram.getUserName() || '';
      }

      // Fetch both route types in parallel
      const [routesV2Response, landmarkRoutesResponse] = await Promise.all([
        fetch(`/api/routes-v2?tab=${tab}`, { headers }),
        fetch('/api/landmark-routes/all', { headers })
      ]);
      
      const routesV2Data = await routesV2Response.json();
      const landmarkRoutesData = await landmarkRoutesResponse.json();
      
      // Get routes-v2 routes
      const v2Routes = (routesV2Data.routes || []).map((r: any) => ({
        ...r,
        routeType: 'v2' // Mark as v2 route
      }));
      
      // Convert landmark routes to compatible format and filter by tab
      const landmarkRoutes = (landmarkRoutesData.routes || [])
        .filter((r: any) => {
          if (tab === 'my') {
            // Show user's own landmark routes in My Routes
            // Also show anonymous routes when user is not authenticated (userId is null/undefined)
            if (!userId) {
              return r.created_by_user_id === 'anonymous';
            }
            return r.created_by_user_id === userId;
          } else if (tab === 'saved') {
            // Saved tab - will be filtered after mapping to include starred routes
            return true; // Let all through, filter later
          } else {
            // Show all public landmark routes (except user's own) in Public Routes
            // When user is anonymous, still exclude anonymous routes from public (they show in My Routes)
            if (!userId) {
              return r.is_public && r.created_by_user_id !== 'anonymous';
            }
            return r.is_public && r.created_by_user_id !== userId;
          }
        })
        .map((r: any) => {
          const routeKey = r.route_id || `landmark-${r.id}`;
          return {
            id: r.id,
            route_id: r.route_id,
            name: r.name,
            description: r.description,
            destination_name: r.facility_id,
            created_by: r.created_by_user_id,
            created_by_username: r.created_by_username,
            is_public: r.is_public,
            publish_status: r.is_public ? 'published' : 'draft',
            status: 'active',
            fork_count: 0,
            waypoint_count: r.landmarks?.length || 0,
            actual_waypoint_count: r.landmarks?.length || 0,
            thumbnailUrl: null,
            total_distance_meters: r.total_distance_meters,
            estimated_duration_minutes: r.estimated_duration_minutes,
            difficulty: r.difficulty,
            surface_type: r.surface_type,
            upvotes: r.upvotes || 0,
            downvotes: r.downvotes || 0,
            landmarks: r.landmarks, // Keep landmarks for navigation
            created_at: r.created_at,
            updated_at: r.updated_at,
            routeType: 'landmark', // Mark as landmark route
            userStarred: starredRouteRouteIds.has(routeKey)
          };
        });
      // Add starred state to v2 routes
      const v2RoutesWithStars = v2Routes.map((r: any) => {
        const routeKey = r.route_id || `v2-${r.id}`;
        return { ...r, userStarred: starredRouteRouteIds.has(routeKey) };
      });
      
      // Merge routes, with landmark routes at the top if they exist
      let allRoutes = [...landmarkRoutes, ...v2RoutesWithStars];
      
      // For 'saved' tab, filter to only starred routes
      if (tab === 'saved') {
        allRoutes = allRoutes.filter(r => r.userStarred);
      }
      
      setRoutes(allRoutes);
      
      // Update counts
      if (tab === 'my') {
        setMyRoutesCount(allRoutes.length);
      } else if (tab === 'public') {
        setPublicRoutesCount(allRoutes.length);
      }
      // savedRoutesCount is updated when starred routes change
      
      console.log('[CommunityRoutes] Loaded routes:', { 
        v2Routes: v2Routes.length, 
        landmarkRoutes: landmarkRoutes.length, 
        total: allRoutes.length,
        tab 
      });
    } catch (error) {
      console.error('Failed to load routes:', error);
      setToast({ open: true, message: 'Failed to load routes', severity: 'error' });
    } finally {
      setRoutesLoading(false);
    }
  };

  const handleRoutesTabChange = (_: React.SyntheticEvent, newValue: RoutesTab) => {
    setRoutesTab(newValue);
    loadRoutes(newValue);
    telegram.hapticFeedback('light');
  };

  // Fork a route
  const handleForkRoute = async (route: any) => {
    const userId = telegram.getUserId();
    if (!userId) {
      setToast({ open: true, message: 'Please log in to fork routes', severity: 'error' });
      return;
    }

    try {
      telegram.hapticFeedback('medium');
      const response = await fetch(`/api/routes-v2/${route.id}/fork`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Telegram-User-Id': userId,
          'X-Telegram-Username': telegram.getUserName() || ''
        }
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        if (response.status === 409) {
          setToast({ open: true, message: 'You already have a fork of this route', severity: 'info' });
        } else {
          throw new Error(data.error || 'Fork failed');
        }
        return;
      }

      setToast({ open: true, message: '🍴 Route forked! Check My Routes', severity: 'success' });
      telegram.hapticFeedback('success');
      
      // Refresh public routes to show updated fork count
      loadRoutes('public');
    } catch (error: any) {
      console.error('Fork error:', error);
      setToast({ open: true, message: error.message || 'Failed to fork route', severity: 'error' });
    }
  };

  // Publish/unpublish route
  const handlePublishRoute = async (route: any, action: 'publish' | 'unpublish' | 'republish') => {
    const userId = telegram.getUserId();
    if (!userId) return;

    try {
      telegram.hapticFeedback('medium');
      
      // Use different API endpoint for landmark routes
      const endpoint = route.routeType === 'landmark' 
        ? `/api/landmark-routes/${route.route_id}/publish`
        : `/api/routes-v2/${route.id}/publish`;
      
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Telegram-User-Id': userId,
          'X-User-Id': userId
        },
        body: JSON.stringify({ action })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error || 'Action failed');
      }

      const messages = {
        publish: '📢 Route published to community!',
        unpublish: '🔒 Route unpublished',
        republish: '🔄 Route republished with updates!'
      };
      
      setToast({ open: true, message: messages[action], severity: 'success' });
      loadRoutes('my');
    } catch (error: any) {
      console.error('Publish error:', error);
      setToast({ open: true, message: error.message || 'Failed to update route', severity: 'error' });
    }
  };

  // Archive (soft delete) route
  const handleArchiveRoute = async (route: any) => {
    const userId = telegram.getUserId();
    if (!userId) return;

    // Confirm with user
    telegram.showConfirm(
      `Archive "${route.name}"? This will hide it from public view but you can still see it in My Routes.`,
      async (confirmed) => {
        if (!confirmed) return;

        try {
          telegram.hapticFeedback('medium');
          const response = await fetch(`/api/routes-v2/${route.id}`, {
            method: 'DELETE',
            headers: {
              'Content-Type': 'application/json',
              'X-Telegram-User-Id': userId
            },
            body: JSON.stringify({ reason: 'User archived' })
          });
          
          const data = await response.json();
          
          if (!response.ok) {
            throw new Error(data.error || 'Archive failed');
          }

          setToast({ open: true, message: '📦 Route archived', severity: 'info' });
          loadRoutes('my');
        } catch (error: any) {
          console.error('Archive error:', error);
          setToast({ open: true, message: error.message || 'Failed to archive route', severity: 'error' });
        }
      }
    );
  };

  // Star/Unstar a route (saved to Telegram CloudStorage)
  const handleStar = async (photo: RoutePhoto) => {
    telegram.hapticFeedback('light');
    const newStarred = new Set(starredRouteIds);
    
    if (newStarred.has(photo.id)) {
      newStarred.delete(photo.id);
      setToast({ open: true, message: '⭐ Removed from starred', severity: 'info' });
    } else {
      newStarred.add(photo.id);
      setToast({ open: true, message: '⭐ Added to starred!', severity: 'success' });
    }
    
    setStarredRouteIds(newStarred);
    await saveStarredRoutes(newStarred);
    
    // Update photo in state
    setPhotos(prev => prev.map(p => 
      p.id === photo.id ? { ...p, userStarred: newStarred.has(photo.id) } : p
    ));
    if (selectedPhoto?.id === photo.id) {
      setSelectedPhoto({ ...selectedPhoto, userStarred: newStarred.has(photo.id) });
    }
  };

  // Fork (download) a route - saves image and copies GPS to clipboard
  const handleFork = async (photo: RoutePhoto) => {
    telegram.hapticFeedback('medium');
    
    try {
      // Copy GPS coordinates to clipboard
      const coords = `${photo.latitude}, ${photo.longitude}`;
      await navigator.clipboard.writeText(coords);
      
      // Open image in new tab for download (or trigger download)
      const link = document.createElement('a');
      link.href = photo.photo_url;
      link.download = `route_${photo.id}_${photo.landmark_type}.jpg`;
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Update fork count via API
      await fetch(`/api/routes/${photo.id}/fork`, { method: 'POST' });
      
      setToast({ 
        open: true, 
        message: '🍴 Route forked! GPS copied to clipboard', 
        severity: 'success' 
      });
      
      // Update local state
      setPhotos(prev => prev.map(p => 
        p.id === photo.id ? { ...p, forks: (p.forks || 0) + 1 } : p
      ));
    } catch (error) {
      console.error('Fork failed:', error);
      setToast({ open: true, message: 'Failed to fork route', severity: 'error' });
    }
  };

  // Vote on a route
  const handleVote = async (photo: RoutePhoto, voteType: 'up' | 'down') => {
    if (votingPhotoId) return; // Prevent double-voting
    
    telegram.hapticFeedback('light');
    setVotingPhotoId(photo.id);
    
    try {
      const userId = telegram.getUserId();
      if (!userId) {
        setToast({ open: true, message: 'Please open from Telegram to vote', severity: 'error' });
        return;
      }
      
      // Determine new vote state
      let newVote: 'up' | 'down' | 'none' = voteType;
      if (photo.userVoted === voteType) {
        newVote = 'none'; // Toggle off
      }
      
      const response = await fetch(`/api/routes/${photo.id}/vote`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: userId, vote: newVote })
      });
      
      const data = await response.json();
      if (data.success) {
        // Update photo in state
        const updatedPhoto = {
          ...photo,
          upvotes: data.upvotes,
          downvotes: data.downvotes,
          score: data.score,
          userVoted: newVote === 'none' ? null : newVote
        };
        
        setPhotos(prev => prev.map(p => p.id === photo.id ? updatedPhoto : p));
        if (selectedPhoto?.id === photo.id) {
          setSelectedPhoto(updatedPhoto);
        }
        
        if (newVote === 'up') {
          telegram.hapticFeedback('success');
        } else if (newVote === 'down') {
          telegram.hapticFeedback('warning');
        }
      }
    } catch (error) {
      console.error('Vote failed:', error);
      setToast({ open: true, message: 'Failed to vote', severity: 'error' });
    } finally {
      setVotingPhotoId(null);
    }
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!isSupportedImageType(file)) {
      setToast({ open: true, message: 'Please select a JPEG or PNG image', severity: 'error' });
      return;
    }
    
    // Check file size (5MB limit)
    if (file.size > 5 * 1024 * 1024) {
      setToast({ open: true, message: 'Photo must be under 5MB', severity: 'error' });
      return;
    }
    
    setPhotoFile(file);
    setPhotoPreview(URL.createObjectURL(file));
    
    // Get device GPS location
    let locationObtained = false;
    
    // Try Telegram location first if available
    const tgWebApp = (window as any).Telegram?.WebApp;
    if (tgWebApp?.requestLocation) {
      try {
        const result = await new Promise<any>((resolve, reject) => {
          tgWebApp.requestLocation((success: boolean, location: any) => {
            if (success && location) {
              resolve(location);
            } else {
              reject(new Error('Telegram location denied'));
            }
          });
          setTimeout(() => reject(new Error('Telegram location timeout')), 10000);
        });
        if (result?.latitude && result?.longitude) {
          const loc = { lat: result.latitude, lng: result.longitude };
          console.log('📍 Got Telegram location:', loc);
          setManualLocation(loc);
          setExtractedGPS(null);
          if (mapInstanceRef.current) {
            mapInstanceRef.current.setCenter(loc);
            mapInstanceRef.current.setZoom(15);
          }
          setToast({ open: true, message: '📍 Using your current location', severity: 'success' });
          locationObtained = true;
        }
      } catch (e) {
        console.warn('Telegram location failed:', e);
      }
    }
    
    // Fallback to browser geolocation
    if (!locationObtained) {
      try {
        const result = await requestPreciseLocation({
          desiredAccuracyMeters: 20,
          refinementTimeoutMs: 8000,
          allowLastKnownFallback: true,
          allowTelegramFallback: true,
        });

        if (!result.fix) {
          throw new Error(result.reason ?? 'Location not available');
        }

        const loc = { lat: result.fix.lat, lng: result.fix.lng };
        console.log('📍 Got precise location:', loc);
        setManualLocation(loc);
        setExtractedGPS(null); // Clear any EXIF GPS

        if (mapInstanceRef.current) {
          mapInstanceRef.current.setCenter(loc);
          mapInstanceRef.current.setZoom(15);
        }

        if (!result.fulfilled && typeof result.fix.accuracy === 'number') {
          setToast({ open: true, message: `📍 Location ±${Math.round(result.fix.accuracy)}m - pinch to adjust if needed`, severity: 'info' });
        } else {
          setToast({ open: true, message: '📍 Using your current location', severity: 'success' });
        }
      } catch (error: any) {
        console.warn('Could not get precise location:', error);
        const errMsg = error instanceof Error ? error.message : 'Location not available';
        setToast({ open: true, message: `${errMsg} - tap the map to set location`, severity: 'info' });

        // Set a default location (center of New Mexico) so user can pan/tap
        const defaultLoc = { lat: 34.5199, lng: -105.8701 };
        setManualLocation(defaultLoc);
        if (mapInstanceRef.current) {
          mapInstanceRef.current.setCenter(defaultLoc);
          mapInstanceRef.current.setZoom(7); // Zoomed out so they can find their area
        }
      }
    }
    
    setAddPhotoStep(1);
  };

  const handleGetCurrentLocation = async () => {
    // Try Telegram location first if available
    const tgWebApp = (window as any).Telegram?.WebApp;
    if (tgWebApp?.requestLocation) {
      try {
        const result = await new Promise<any>((resolve, reject) => {
          tgWebApp.requestLocation((success: boolean, location: any) => {
            if (success && location) {
              resolve(location);
            } else {
              reject(new Error('Telegram location denied'));
            }
          });
          // Timeout fallback
          setTimeout(() => reject(new Error('Telegram location timeout')), 10000);
        });
        if (result?.latitude && result?.longitude) {
          const loc = { lat: result.latitude, lng: result.longitude };
          setManualLocation(loc);
          if (mapInstanceRef.current) {
            mapInstanceRef.current.setCenter(loc);
            mapInstanceRef.current.setZoom(15);
          }
          setToast({ open: true, message: '📍 Using current location', severity: 'success' });
          return;
        }
      } catch (e) {
        console.warn('Telegram location failed, trying browser:', e);
      }
    }
    
    try {
      const result = await requestPreciseLocation({
        desiredAccuracyMeters: 25,
        refinementTimeoutMs: 7000,
        allowLastKnownFallback: true,
        allowTelegramFallback: true,
      });

      if (!result.fix) {
        setToast({ open: true, message: 'Location unavailable - tap the map instead', severity: 'info' });
        return;
      }

      const loc = { lat: result.fix.lat, lng: result.fix.lng };
      setManualLocation(loc);
      if (mapInstanceRef.current) {
        mapInstanceRef.current.setCenter(loc);
        mapInstanceRef.current.setZoom(15);
      }
      const message = !result.fulfilled && typeof result.fix.accuracy === 'number'
        ? `📍 Using current location (±${Math.round(result.fix.accuracy)}m)`
        : '📍 Using current location';
      setToast({ open: true, message, severity: 'success' });
    } catch (error) {
      console.warn('Precise location fallback failed:', error);
      setToast({ open: true, message: 'Location unavailable - tap the map instead', severity: 'info' });
    }
  };

  const handleSubmitPhoto = async () => {
    telegram.hapticFeedback('light');
    const location = extractedGPS || manualLocation;
    if (!photoFile || !location) {
      telegram.hapticFeedback('error');
      setToast({ open: true, message: 'Photo and location required', severity: 'error' });
      return;
    }
    
    setSubmitting(true);
    
    try {
      // Compress photo
      const compressedPhoto = await compressImageForUpload(photoFile, 5);
      const thumbnail = await createThumbnail(photoFile, 300);
      
      // Prepare form data
      const formData = new FormData();
      formData.append('photo', compressedPhoto, 'photo.jpg');
      formData.append('thumbnail', thumbnail, 'thumb.jpg');
      formData.append('latitude', location.lat.toString());
      formData.append('longitude', location.lng.toString());
      formData.append('step_number', stepNumber.toString());
      formData.append('landmark_type', landmarkType);
      formData.append('description', description);
      
      // Include API number for destination/facility landmarks
      if (landmarkType === 'destination' && apiNumber) {
        formData.append('api_number', apiNumber);
      }
      
      // Get Telegram user info if available
      if (telegram.webApp?.initDataUnsafe?.user) {
        const user = telegram.webApp.initDataUnsafe.user;
        formData.append('submitted_by', user.id.toString());
        formData.append('submitted_by_username', user.username || user.first_name || '');
      }
      
      const response = await fetch('/api/routes', {
        method: 'POST',
        body: formData
      });
      
      const data = await response.json();
      console.log('Photo submit response:', data);
      
      if (data.success) {
        // Store photo info for sharing
        setLastSubmittedPhotoId(data.photo_id);
        setLastSubmittedLocation({ 
          lat: location.lat, 
          lng: location.lng, 
          landmarkType, 
          description 
        });
        setShowShareDialog(true); // Show share dialog
        telegram.hapticFeedback('success');
      } else {
        throw new Error(data.error || 'Unknown error');
      }
      
    } catch (error) {
      console.error('Failed to submit photo:', error);
      const errorMsg = error instanceof Error ? error.message : 'Failed to submit photo';
      setToast({ open: true, message: errorMsg, severity: 'error' });
      telegram.hapticFeedback('error');
    } finally {
      setSubmitting(false);
    }
  };

  const resetAddPhotoForm = () => {
    setPhotoFile(null);
    setPhotoPreview(null);
    setExtractedGPS(null);
    setManualLocation(null);
    setStepNumber(1);
    setLandmarkType('turnoff');
    setDescription('');
    setApiNumber('');
    setAddPhotoStep(0);
  };

  // Note: startNavigateRoute kept for potential future use but called from navigate mode
  const startNavigateRoute = useCallback((facilityPhotos?: RoutePhoto[]) => {
    const photosToNavigate = facilityPhotos || photos;
    if (photosToNavigate.length === 0) {
      setToast({ open: true, message: 'No route photos to navigate', severity: 'info' });
      return;
    }
    
    // Sort by step number
    const sorted = [...photosToNavigate].sort((a, b) => a.step_number - b.step_number);
    setRoutePhotos(sorted);
    setCurrentRouteIndex(0);
    setNavigateMode(true);
    
    // Center on first photo
    if (mapInstanceRef.current && sorted.length > 0) {
      mapInstanceRef.current.setCenter({ lat: sorted[0].latitude, lng: sorted[0].longitude });
      mapInstanceRef.current.setZoom(14);
    }
  }, [photos]);
  
  // Suppress unused warning - function available for future use
  void startNavigateRoute;

  const navigateToStep = (index: number) => {
    if (index < 0 || index >= routePhotos.length) return;
    setCurrentRouteIndex(index);
    
    const photo = routePhotos[index];
    if (mapInstanceRef.current) {
      mapInstanceRef.current.panTo({ lat: photo.latitude, lng: photo.longitude });
    }
  };

  const openInMaps = (lat: number, lng: number) => {
    window.open(`https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`, '_blank');
  };

  // Share functionality
  const generateShareLink = (photoId: number) => {
    return `https://ziawellnav.pages.dev/?route=${photoId}`;
  };

  const handleShare = async () => {
    if (!lastSubmittedPhotoId || !lastSubmittedLocation) return;
    
    const shareLink = generateShareLink(lastSubmittedPhotoId);
    const landmark = LANDMARK_TYPES.find(l => l.value === lastSubmittedLocation.landmarkType);
    const shareText = `${landmark?.icon || '📍'} ${landmark?.label || 'Route Marker'}\n${lastSubmittedLocation.description || 'Check this location!'}\n\n${shareLink}`;
    
    try {
      if (navigator.share) {
        await navigator.share({
          title: 'Route Location - ZiaWellNav',
          text: shareText,
          url: shareLink
        });
      } else {
        await navigator.clipboard.writeText(shareText);
        telegram.hapticFeedback('success');
        setToast({ open: true, message: 'Link copied to clipboard!', severity: 'success' });
      }
    } catch (error) {
      // User cancelled share or error
      console.log('Share cancelled or failed:', error);
    }
  };

  const handleCopyLink = async () => {
    if (!lastSubmittedPhotoId) return;
    try {
      await navigator.clipboard.writeText(generateShareLink(lastSubmittedPhotoId));
      setToast({ open: true, message: 'Link copied!', severity: 'success' });
      telegram.hapticFeedback('success');
    } catch (error) {
      setToast({ open: true, message: 'Failed to copy link', severity: 'error' });
    }
  };

  const closeShareDialog = () => {
    setShowShareDialog(false);
    resetAddPhotoForm();
    setShowAddPhoto(false);
    loadPhotos(); // Refresh the map markers
  };

  const location = extractedGPS || manualLocation;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen
      PaperProps={{
        sx: {
          backgroundColor: cyber.bgPrimary,
          backgroundImage: 'none'
        }
      }}
    >
      {/* Header */}
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        p: 2,
        borderBottom: 'none',
        backgroundColor: cyber.bgSecondary
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ZiaCompassIcon sx={{ color: cyber.neonGreen, fontSize: 28 }} />
          <Typography variant="h6" sx={{ fontWeight: 700, color: cyber.textPrimary }}>
            Caliche Routes
          </Typography>
        </Box>
        <IconButton onClick={() => { telegram.hapticFeedback('light'); onClose(); }} sx={{ color: cyber.textSecondary }}>
          <CloseIcon />
        </IconButton>
      </Box>

      {/* View Mode Toggle removed - defaulting to Routes view */}

      {/* Filter Tabs - Different for each view mode */}
      {viewMode === 'landmarks' ? (
        <Tabs 
          value={activeFilter} 
          onChange={handleFilterChange}
          sx={{ 
            borderBottom: `1px solid ${cyber.borderDefault}`,
            backgroundColor: cyber.bgSecondary,
            minHeight: 44,
            px: 1,
            '& .MuiTab-root': {
              color: cyber.textSecondary,
              minHeight: 44,
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.85rem',
              '&.Mui-selected': {
                color: cyber.neonGreen
              }
            },
            '& .MuiTabs-indicator': {
              backgroundColor: cyber.neonGreen
            }
          }}
        >
          <Tab 
            value="all" 
            icon={<PublicIcon sx={{ fontSize: 18 }} />}
            iconPosition="start"
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                All Public
                <Chip 
                  label={photos.length} 
                  size="small" 
                  sx={{ 
                    height: 20, 
                    fontSize: '0.7rem',
                    backgroundColor: activeFilter === 'all' ? cyber.neonGreen + '30' : 'rgba(148, 163, 184, 0.2)',
                    color: activeFilter === 'all' ? cyber.neonGreen : cyber.textSecondary
                  }}
                />
              </Box>
            }
          />
          <Tab 
            value="mine" 
            icon={<PersonIcon sx={{ fontSize: 18 }} />}
            iconPosition="start"
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                My Posts
                <Chip 
                  label={myPostsCount} 
                  size="small" 
                  sx={{ 
                    height: 20, 
                    fontSize: '0.7rem',
                    backgroundColor: activeFilter === 'mine' ? cyber.neonBlue + '30' : 'rgba(148, 163, 184, 0.2)',
                    color: activeFilter === 'mine' ? cyber.neonBlue : cyber.textSecondary
                  }}
                />
              </Box>
            }
          />
        </Tabs>
      ) : (
        <Tabs 
          value={routesTab} 
          onChange={handleRoutesTabChange}
          sx={{ 
            borderBottom: `1px solid ${cyber.borderDefault}`,
            backgroundColor: cyber.bgSecondary,
            minHeight: 44,
            px: 1,
            '& .MuiTab-root': {
              color: cyber.textSecondary,
              minHeight: 44,
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.85rem',
              '&.Mui-selected': {
                color: cyber.neonGreen
              }
            },
            '& .MuiTabs-indicator': {
              backgroundColor: cyber.neonGreen
            }
          }}
        >
          <Tab 
            value="my" 
            icon={<PersonIcon sx={{ fontSize: 18 }} />}
            iconPosition="start"
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                My Routes
                <Chip 
                  label={myRoutesCount} 
                  size="small" 
                  sx={{ 
                    height: 20, 
                    fontSize: '0.7rem',
                    backgroundColor: routesTab === 'my' ? cyber.neonBlue + '30' : 'rgba(148, 163, 184, 0.2)',
                    color: routesTab === 'my' ? cyber.neonBlue : cyber.textSecondary
                  }}
                />
              </Box>
            }
          />
          <Tab 
            value="saved" 
            icon={<StarIcon sx={{ fontSize: 18 }} />}
            iconPosition="start"
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                Saved
                <Chip 
                  label={savedRoutesCount} 
                  size="small" 
                  sx={{ 
                    height: 20, 
                    fontSize: '0.7rem',
                    backgroundColor: routesTab === 'saved' ? '#fbbf24' + '30' : 'rgba(148, 163, 184, 0.2)',
                    color: routesTab === 'saved' ? '#fbbf24' : cyber.textSecondary
                  }}
                />
              </Box>
            }
          />
          <Tab 
            value="public" 
            icon={<PublicIcon sx={{ fontSize: 18 }} />}
            iconPosition="start"
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                Public
                <Chip 
                  label={publicRoutesCount} 
                  size="small" 
                  sx={{ 
                    height: 20, 
                    fontSize: '0.7rem',
                    backgroundColor: routesTab === 'public' ? cyber.neonGreen + '30' : 'rgba(148, 163, 184, 0.2)',
                    color: routesTab === 'public' ? cyber.neonGreen : cyber.textSecondary
                  }}
                />
              </Box>
            }
          />
        </Tabs>
      )}

      <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column', height: '100%' }}>
        {/* Landmarks View: Map with photo markers */}
        <Box sx={{ flex: 1, minHeight: '50vh', width: '100%', position: 'relative', display: viewMode === 'landmarks' ? 'block' : 'none' }}>
          {/* Map */}
          <Box
            ref={mapRef}
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              width: '100%',
              height: '100%'
            }}
          />

          {/* Custom Cyberpunk Zoom Controls - Right Side (Large for Gloves) */}
          <Box sx={{
            position: 'absolute',
            right: 16,
            top: '50%',
            transform: 'translateY(-50%)',
            zIndex: 1050,
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
          }}>
            {/* Zoom In */}
            <IconButton
              onClick={() => {
                const map = mapInstanceRef.current;
                if (map) {
                  const zoom = map.getZoom() || 10;
                  map.setZoom(Math.min(zoom + 1, 21));
                  telegram.hapticFeedback('light');
                }
              }}
              sx={{
                width: 56,
                height: 56,
                backgroundColor: 'rgba(15, 23, 42, 0.9)',
                backdropFilter: 'blur(12px)',
                border: `2px solid ${cyber.neonCyan}`,
                borderRadius: 2,
                color: cyber.neonCyan,
                boxShadow: `0 0 15px ${cyber.neonCyan}40, inset 0 0 10px ${cyber.neonCyan}15`,
                transition: 'all 0.2s ease',
                '&:hover': {
                  backgroundColor: 'rgba(15, 23, 42, 0.95)',
                  borderColor: cyber.neonBlue,
                  color: cyber.neonBlue,
                  boxShadow: `0 0 25px ${cyber.neonBlue}60, inset 0 0 15px ${cyber.neonBlue}25`,
                  transform: 'scale(1.05)',
                },
                '&:active': {
                  transform: 'scale(0.95)',
                  boxShadow: `0 0 30px ${cyber.neonGreen}70, inset 0 0 20px ${cyber.neonGreen}30`,
                  borderColor: cyber.neonGreen,
                  color: cyber.neonGreen,
                },
              }}
            >
              <AddIcon sx={{ fontSize: 32, fontWeight: 700 }} />
            </IconButton>

            {/* Zoom Out */}
            <IconButton
              onClick={() => {
                const map = mapInstanceRef.current;
                if (map) {
                  const zoom = map.getZoom() || 10;
                  map.setZoom(Math.max(zoom - 1, 1));
                  telegram.hapticFeedback('light');
                }
              }}
              sx={{
                width: 56,
                height: 56,
                backgroundColor: 'rgba(15, 23, 42, 0.9)',
                backdropFilter: 'blur(12px)',
                border: `2px solid ${cyber.neonCyan}`,
                borderRadius: 2,
                color: cyber.neonCyan,
                boxShadow: `0 0 15px ${cyber.neonCyan}40, inset 0 0 10px ${cyber.neonCyan}15`,
                transition: 'all 0.2s ease',
                '&:hover': {
                  backgroundColor: 'rgba(15, 23, 42, 0.95)',
                  borderColor: cyber.neonBlue,
                  color: cyber.neonBlue,
                  boxShadow: `0 0 25px ${cyber.neonBlue}60, inset 0 0 15px ${cyber.neonBlue}25`,
                  transform: 'scale(1.05)',
                },
                '&:active': {
                  transform: 'scale(0.95)',
                  boxShadow: `0 0 30px ${cyber.neonGreen}70, inset 0 0 20px ${cyber.neonGreen}30`,
                  borderColor: cyber.neonGreen,
                  color: cyber.neonGreen,
                },
              }}
            >
              <RemoveIcon sx={{ fontSize: 32, fontWeight: 700 }} />
            </IconButton>

            {/* Map Style Toggle */}
            <IconButton
              onClick={() => {
                const map = mapInstanceRef.current;
                if (map) {
                  const currentType = map.getMapTypeId();
                  const types = ['satellite', 'hybrid', 'terrain', 'roadmap'] as const;
                  const currentIdx = types.indexOf(currentType as typeof types[number]);
                  map.setMapTypeId(types[(currentIdx + 1) % types.length]);
                  telegram.hapticFeedback('light');
                }
              }}
              sx={{
                width: 56,
                height: 56,
                backgroundColor: 'rgba(15, 23, 42, 0.9)',
                backdropFilter: 'blur(12px)',
                border: `2px solid ${cyber.neonPurple}`,
                borderRadius: 2,
                color: cyber.neonPurple,
                boxShadow: `0 0 15px ${cyber.neonPurple}40, inset 0 0 10px ${cyber.neonPurple}15`,
                transition: 'all 0.2s ease',
                '&:hover': {
                  backgroundColor: 'rgba(15, 23, 42, 0.95)',
                  borderColor: cyber.neonPink,
                  color: cyber.neonPink,
                  boxShadow: `0 0 25px ${cyber.neonPink}60, inset 0 0 15px ${cyber.neonPink}25`,
                  transform: 'scale(1.05)',
                },
                '&:active': {
                  transform: 'scale(0.95)',
                  boxShadow: `0 0 30px ${cyber.neonGreen}70, inset 0 0 20px ${cyber.neonGreen}30`,
                },
              }}
            >
              <LayersIcon sx={{ fontSize: 28 }} />
            </IconButton>
          </Box>
        </Box>

        {/* Loading overlay */}
        {loading && (
          <Box sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            zIndex: 1000
          }}>
            <CyberSpinner size={48} />
          </Box>
        )}



        {/* Add Photo Panel */}
        {showAddPhoto && (
          <Box sx={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            backgroundColor: cyber.bgSecondary,
            borderTopLeftRadius: 20,
            borderTopRightRadius: 20,
            p: 2,
            maxHeight: '60vh',
            overflowY: 'auto',
            boxShadow: '0 -4px 20px rgba(0,0,0,0.3)'
          }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" sx={{ color: cyber.textPrimary, fontWeight: 700 }}>
                📸 Add Route Photo
              </Typography>
              <IconButton onClick={() => { setShowAddPhoto(false); resetAddPhotoForm(); }}>
                <CloseIcon sx={{ color: cyber.textSecondary }} />
              </IconButton>
            </Box>

            <Stepper activeStep={addPhotoStep} orientation="vertical">
              {/* Step 1: Select Photo */}
              <Step>
                <StepLabel>
                  <Typography sx={{ color: cyber.textPrimary }}>Select Photo</Typography>
                </StepLabel>
                <StepContent>
                  {/* Camera input - opens camera directly */}
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/heic"
                    capture="environment"
                    ref={cameraInputRef}
                    onChange={handleFileSelect}
                    style={{ display: 'none' }}
                  />
                  {/* Gallery input - opens photo library */}
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/heic"
                    ref={galleryInputRef}
                    onChange={handleFileSelect}
                    style={{ display: 'none' }}
                  />
                  
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 2 }}>
                    <Button
                      variant="contained"
                      startIcon={<PhotoCameraIcon />}
                      onClick={handleCameraClick}
                      sx={{ 
                        backgroundColor: cyber.neonGreen,
                        color: '#000',
                        fontWeight: 600,
                        py: 1.5,
                        '&:hover': { backgroundColor: '#4ade80' }
                      }}
                    >
                      📷 Take Photo
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<PhotoLibraryIcon />}
                      onClick={() => galleryInputRef.current?.click()}
                      sx={{ 
                        borderColor: 'rgba(96, 165, 250, 0.5)',
                        color: cyber.neonBlue,
                        py: 1.5,
                        '&:hover': { 
                          borderColor: cyber.neonBlue,
                          backgroundColor: 'rgba(96, 165, 250, 0.1)' 
                        }
                      }}
                    >
                      🖼️ Choose from Gallery
                    </Button>
                  </Box>
                  
                  <Typography variant="caption" sx={{ color: cyber.textMuted }}>
                    📍 GPS coordinates will be automatically extracted from the photo if available
                  </Typography>
                </StepContent>
              </Step>

              {/* Step 2: Confirm Location */}
              <Step>
                <StepLabel>
                  <Typography sx={{ color: cyber.textPrimary }}>Confirm Location</Typography>
                </StepLabel>
                <StepContent>
                  {photoPreview && (
                    <Box sx={{ mb: 2 }}>
                      <img 
                        src={photoPreview} 
                        alt="Preview" 
                        style={{ 
                          width: '100%', 
                          maxHeight: 150, 
                          objectFit: 'cover', 
                          borderRadius: 8 
                        }} 
                      />
                    </Box>
                  )}
                  
                  {extractedGPS ? (
                    <Alert severity="success" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Chip
                        label={`📍 GPS: ${extractedGPS.lat.toFixed(5)}, ${extractedGPS.lng.toFixed(5)}`}
                        size="small"
                        onClick={() => window.open(`https://www.google.com/maps/search/?api=1&query=${extractedGPS.lat},${extractedGPS.lng}`, '_blank')}
                        sx={{
                          backgroundColor: 'rgba(251, 191, 36, 0.15)',
                          color: '#fbbf24',
                          cursor: 'pointer',
                          fontWeight: 600,
                          '&:hover': { backgroundColor: 'rgba(251, 191, 36, 0.3)' }
                        }}
                      />
                    </Alert>
                  ) : manualLocation ? (
                    <Alert severity="info" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Chip
                        label={`📍 Location: ${manualLocation.lat.toFixed(5)}, ${manualLocation.lng.toFixed(5)}`}
                        size="small"
                        onClick={() => window.open(`https://www.google.com/maps/search/?api=1&query=${manualLocation.lat},${manualLocation.lng}`, '_blank')}
                        sx={{
                          backgroundColor: 'rgba(251, 191, 36, 0.15)',
                          color: '#fbbf24',
                          cursor: 'pointer',
                          fontWeight: 600,
                          '&:hover': { backgroundColor: 'rgba(251, 191, 36, 0.3)' }
                        }}
                      />
                    </Alert>
                  ) : (
                    <Box sx={{ mb: 2 }}>
                      <Alert severity="warning" sx={{ mb: 1 }}>
                        No GPS in photo. Set location manually:
                      </Alert>
                      <Button
                        variant="outlined"
                        startIcon={<MyLocationIcon />}
                        onClick={handleGetCurrentLocation}
                        sx={{ mr: 1 }}
                      >
                        Use My Location
                      </Button>
                      <Typography variant="caption" sx={{ color: cyber.textMuted, display: 'block', mt: 1 }}>
                        Or tap on the map to set location
                      </Typography>
                    </Box>
                  )}
                  
                  <Button 
                    variant="contained" 
                    onClick={() => setAddPhotoStep(2)}
                    disabled={!location}
                    sx={{ backgroundColor: cyber.neonBlue }}
                  >
                    Continue
                  </Button>
                </StepContent>
              </Step>

              {/* Step 3: Add Details */}
              <Step>
                <StepLabel>
                  <Typography sx={{ color: cyber.textPrimary }}>Add Details</Typography>
                </StepLabel>
                <StepContent>
                  <FormControl fullWidth sx={{ mb: 2 }}>
                    <InputLabel sx={{ color: cyber.textSecondary }}>Landmark Type</InputLabel>
                    <Select
                      value={landmarkType}
                      onChange={(e) => setLandmarkType(e.target.value)}
                      label="Landmark Type"
                      sx={{ 
                        color: cyber.textPrimary,
                        '.MuiOutlinedInput-notchedOutline': { borderColor: cyber.borderDefault }
                      }}
                    >
                      {LANDMARK_TYPES.map(type => (
                        <MenuItem key={type.value} value={type.value}>
                          {type.icon} {type.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <TextField
                    label="Step Number"
                    type="number"
                    value={stepNumber}
                    onChange={(e) => setStepNumber(parseInt(e.target.value) || 1)}
                    fullWidth
                    sx={{ mb: 2 }}
                    InputProps={{ inputProps: { min: 1, max: 99 } }}
                    helperText="Order in the route (1 = first turn, 2 = second, etc.)"
                  />

                  {/* API Number field - shown when destination/facility is selected */}
                  {landmarkType === 'destination' && (
                    <TextField
                      label="API Number"
                      value={apiNumber}
                      onChange={(e) => setApiNumber(e.target.value)}
                      fullWidth
                      sx={{ mb: 2 }}
                      placeholder="e.g., 30-025-12345"
                      helperText="Well/facility API number (for the final destination)"
                    />
                  )}

                  <TextField
                    label="Description"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    fullWidth
                    multiline
                    rows={2}
                    sx={{ mb: 2 }}
                    placeholder={landmarkType === 'destination' 
                      ? "e.g., Devon Well Pad #5, Tank Battery on left"
                      : "e.g., Turn left at blue gate, code 1234"}
                  />

                  <Button
                    variant="contained"
                    onClick={handleSubmitPhoto}
                    disabled={submitting}
                    fullWidth
                    sx={{ 
                      backgroundColor: cyber.neonGreen,
                      py: 1.5,
                      fontSize: '1rem'
                    }}
                  >
                    {submitting ? <CyberSpinner size={24} /> : '📤 Submit for Review'}
                  </Button>
                </StepContent>
              </Step>
            </Stepper>
          </Box>
        )}

        {/* Navigate Route Panel */}
        {navigateMode && routePhotos.length > 0 && (
          <Box sx={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            backgroundColor: cyber.bgSecondary,
            borderTopLeftRadius: 20,
            borderTopRightRadius: 20,
            p: 2,
            boxShadow: '0 -4px 20px rgba(0,0,0,0.3)'
          }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" sx={{ color: cyber.textPrimary, fontWeight: 700 }}>
                🧭 Route Navigation
              </Typography>
              <IconButton onClick={() => setNavigateMode(false)}>
                <CloseIcon sx={{ color: cyber.textSecondary }} />
              </IconButton>
            </Box>

            {/* Current photo */}
            <Card sx={{ backgroundColor: cyber.bgCard, mb: 2, p: 1 }}>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <img
                  src={routePhotos[currentRouteIndex].photo_url}
                  alt="Route"
                  style={{ width: 120, height: 90, objectFit: 'cover', borderRadius: 8 }}
                />
                <Box sx={{ flex: 1 }}>
                  <Typography variant="subtitle2" sx={{ color: cyber.textPrimary, fontWeight: 700 }}>
                    Step {routePhotos[currentRouteIndex].step_number}: {
                      LANDMARK_TYPES.find(l => l.value === routePhotos[currentRouteIndex].landmark_type)?.label
                    }
                  </Typography>
                  {/* Show API number for destination/facility landmarks */}
                  {routePhotos[currentRouteIndex].api_number && (
                    <Typography variant="body2" sx={{ color: cyber.neonGreen, fontWeight: 600 }}>
                      API: {routePhotos[currentRouteIndex].api_number}
                    </Typography>
                  )}
                  <Typography variant="body2" sx={{ color: cyber.textSecondary }}>
                    {routePhotos[currentRouteIndex].description || 'No description'}
                  </Typography>
                  <Button
                    size="small"
                    startIcon={<DirectionsIcon />}
                    onClick={() => openInMaps(
                      routePhotos[currentRouteIndex].latitude,
                      routePhotos[currentRouteIndex].longitude
                    )}
                    sx={{ mt: 1, color: cyber.neonBlue }}
                  >
                    Get Directions
                  </Button>
                </Box>
              </Box>
            </Card>

            {/* Navigation controls */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <IconButton
                onClick={() => navigateToStep(currentRouteIndex - 1)}
                disabled={currentRouteIndex === 0}
                sx={{ color: cyber.neonBlue }}
              >
                <ChevronLeftIcon sx={{ fontSize: 32 }} />
              </IconButton>
              
              <Typography sx={{ color: cyber.textPrimary }}>
                {currentRouteIndex + 1} / {routePhotos.length}
              </Typography>
              
              <IconButton
                onClick={() => navigateToStep(currentRouteIndex + 1)}
                disabled={currentRouteIndex === routePhotos.length - 1}
                sx={{ color: cyber.neonBlue }}
              >
                <ChevronRightIcon sx={{ fontSize: 32 }} />
              </IconButton>
            </Box>
          </Box>
        )}

        {/* Selected Photo Modal */}
        {selectedPhoto && !navigateMode && (
          <Box sx={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            backgroundColor: cyber.bgSecondary,
            borderTopLeftRadius: 20,
            borderTopRightRadius: 20,
            p: 2,
            maxHeight: '60vh',
            overflowY: 'auto',
            boxShadow: '0 -4px 20px rgba(0,0,0,0.3)'
          }}>
            {/* Header with close and trust score */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
              <Box>
                <Typography variant="h6" sx={{ color: cyber.textPrimary, mb: 0.5 }}>
                  {LANDMARK_TYPES.find(l => l.value === selectedPhoto.landmark_type)?.icon}{' '}
                  {LANDMARK_TYPES.find(l => l.value === selectedPhoto.landmark_type)?.label}
                </Typography>
                {/* Trust Score Badge */}
                {(() => {
                  const trust = getTrustLevel(selectedPhoto.score || 0);
                  return (
                    <Chip
                      icon={<span style={{ marginLeft: 8 }}>{trust.icon}</span>}
                      label={`${trust.label} (${selectedPhoto.score >= 0 ? '+' : ''}${selectedPhoto.score || 0})`}
                      size="small"
                      sx={{
                        backgroundColor: trust.bgColor,
                        color: trust.color,
                        fontWeight: 700,
                        fontSize: '0.75rem',
                        '& .MuiChip-icon': { fontSize: '1rem' }
                      }}
                    />
                  );
                })()}
              </Box>
              <IconButton onClick={() => setSelectedPhoto(null)} size="small">
                <CloseIcon sx={{ color: cyber.textSecondary }} />
              </IconButton>
            </Box>
            
            {/* Photo */}
            <img
              src={selectedPhoto.photo_url}
              alt="Route photo"
              style={{ width: '100%', maxHeight: 180, objectFit: 'cover', borderRadius: 8, marginBottom: 12 }}
            />
            
            {/* Vote/Star/Fork Actions Row */}
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'space-between',
              mb: 2,
              p: 1.5,
              backgroundColor: 'rgba(30, 41, 59, 0.5)',
              borderRadius: 2,
              border: `1px solid ${cyber.borderDefault}`
            }}>
              {/* Voting */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Tooltip title="Good route - thumbs up!">
                  <IconButton 
                    onClick={() => handleVote(selectedPhoto, 'up')}
                    disabled={votingPhotoId === selectedPhoto.id}
                    sx={{ 
                      color: selectedPhoto.userVoted === 'up' ? '#22c55e' : cyber.textSecondary,
                      '&:hover': { backgroundColor: 'rgba(34, 197, 94, 0.15)' }
                    }}
                  >
                    {selectedPhoto.userVoted === 'up' ? <ThumbUpIcon /> : <ThumbUpOutlinedIcon />}
                  </IconButton>
                </Tooltip>
                <Typography sx={{ 
                  fontWeight: 700, 
                  fontSize: '1.1rem',
                  color: (selectedPhoto.score || 0) > 0 ? '#22c55e' : (selectedPhoto.score || 0) < 0 ? '#ef4444' : cyber.textSecondary,
                  minWidth: 35,
                  textAlign: 'center'
                }}>
                  {(selectedPhoto.score || 0) > 0 ? '+' : ''}{selectedPhoto.score || 0}
                </Typography>
                <Tooltip title="Bad route - thumbs down">
                  <IconButton 
                    onClick={() => handleVote(selectedPhoto, 'down')}
                    disabled={votingPhotoId === selectedPhoto.id}
                    sx={{ 
                      color: selectedPhoto.userVoted === 'down' ? '#ef4444' : cyber.textSecondary,
                      '&:hover': { backgroundColor: 'rgba(239, 68, 68, 0.15)' }
                    }}
                  >
                    {selectedPhoto.userVoted === 'down' ? <ThumbDownIcon /> : <ThumbDownOutlinedIcon />}
                  </IconButton>
                </Tooltip>
              </Box>

              {/* Star */}
              <Tooltip title={selectedPhoto.userStarred ? 'Remove star' : 'Star this route'}>
                <IconButton 
                  onClick={() => handleStar(selectedPhoto)}
                  sx={{ 
                    color: selectedPhoto.userStarred ? '#fbbf24' : cyber.textSecondary,
                    '&:hover': { backgroundColor: 'rgba(251, 191, 36, 0.15)' }
                  }}
                >
                  {selectedPhoto.userStarred ? <StarIcon /> : <StarBorderIcon />}
                </IconButton>
              </Tooltip>

              {/* Fork (Download) */}
              <Tooltip title="Fork - Download & copy GPS">
                <IconButton 
                  onClick={() => handleFork(selectedPhoto)}
                  sx={{ 
                    color: cyber.neonBlue,
                    '&:hover': { backgroundColor: 'rgba(96, 165, 250, 0.15)' }
                  }}
                >
                  <Badge badgeContent={selectedPhoto.forks || 0} color="primary" max={99}>
                    <ForkRightIcon />
                  </Badge>
                </IconButton>
              </Tooltip>
            </Box>
            
            {/* Description */}
            {selectedPhoto.description && (
              <Typography sx={{ color: cyber.textSecondary, mb: 1.5, fontSize: '0.9rem' }}>
                📝 {selectedPhoto.description}
              </Typography>
            )}
            
            {/* API Number for destination/facility */}
            {selectedPhoto.api_number && (
              <Chip
                label={`🏁 API: ${selectedPhoto.api_number}`}
                size="small"
                sx={{
                  mb: 1.5,
                  backgroundColor: 'rgba(34, 197, 94, 0.15)',
                  color: '#22c55e',
                  fontWeight: 700,
                  fontSize: '0.85rem'
                }}
              />
            )}
            
            {/* Posted by */}
            {selectedPhoto.submitted_by_username && (
              <Typography sx={{ color: cyber.textMuted, mb: 1.5, fontSize: '0.8rem' }}>
                📤 Posted by @{selectedPhoto.submitted_by_username}
              </Typography>
            )}
            
            {/* GPS Chip */}
            <Chip
              label={`📍 ${selectedPhoto.latitude.toFixed(5)}, ${selectedPhoto.longitude.toFixed(5)}`}
              size="small"
              onClick={() => openInMaps(selectedPhoto.latitude, selectedPhoto.longitude)}
              sx={{
                mb: 2,
                backgroundColor: 'rgba(251, 191, 36, 0.15)',
                color: '#fbbf24',
                cursor: 'pointer',
                fontWeight: 600,
                '&:hover': { backgroundColor: 'rgba(251, 191, 36, 0.3)' }
              }}
            />
            
            {/* Action Buttons */}
            <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
              <Button
                variant="contained"
                startIcon={<DirectionsIcon />}
                onClick={() => openInMaps(selectedPhoto.latitude, selectedPhoto.longitude)}
                fullWidth
                sx={{ backgroundColor: cyber.neonBlue }}
              >
                Directions
              </Button>
              <Button
                variant="outlined"
                startIcon={<ShareIcon />}
                onClick={async () => {
                  const landmark = LANDMARK_TYPES.find(l => l.value === selectedPhoto.landmark_type);
                  const shareLink = generateShareLink(selectedPhoto.id);
                  const trust = getTrustLevel(selectedPhoto.score || 0);
                  const shareText = `${landmark?.icon || '📍'} ${landmark?.label || 'Route Marker'} ${trust.icon}\nTrust: ${trust.label} (${selectedPhoto.score >= 0 ? '+' : ''}${selectedPhoto.score || 0})\n${selectedPhoto.description || ''}\n\n${shareLink}`;
                  
                  try {
                    if (navigator.share) {
                      await navigator.share({
                        title: 'Route Location - ZiaWellNav',
                        text: shareText,
                        url: shareLink
                      });
                    } else {
                      await navigator.clipboard.writeText(shareText);
                      telegram.hapticFeedback('success');
                      setToast({ open: true, message: 'Link copied!', severity: 'success' });
                    }
                  } catch (error) {
                    console.log('Share cancelled:', error);
                  }
                }}
                fullWidth
                sx={{ borderColor: cyber.borderDefault, color: cyber.textPrimary }}
              >
                Share
              </Button>
            </Box>
          </Box>
        )}

        {/* Routes View: List of waypoint-based routes */}
        {viewMode === 'routes' && (
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            {/* Routes Loading */}
            {routesLoading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
                <CyberSpinner size={48} />
              </Box>
            )}

            {/* Routes List */}
            {!routesLoading && routes.length === 0 && (
              <Box sx={{ 
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center', 
                justifyContent: 'center', 
                flex: 1,
                p: 4,
                textAlign: 'center'
              }}>
                <RouteIcon sx={{ fontSize: 64, color: cyber.textMuted, mb: 2 }} />
                <Typography sx={{ color: cyber.textSecondary, fontSize: '1.1rem', mb: 1 }}>
                  {routesTab === 'my' ? 'No routes created yet' : routesTab === 'saved' ? 'No saved routes yet' : 'No public routes available'}
                </Typography>
                <Typography sx={{ color: cyber.textMuted, fontSize: '0.9rem', mb: 3 }}>
                  {routesTab === 'my' 
                    ? 'Create a route from the Favorites Map using the route creation FAB'
                    : routesTab === 'saved'
                    ? 'Star routes from Public Routes to save them here for quick access'
                    : 'Be the first to share a route with the community!'}
                </Typography>
              </Box>
            )}

            {!routesLoading && routes.length > 0 && (
              <Box sx={{ overflow: 'auto', flex: 1, pb: 2, px: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
                {routes.map((route) => {
                  // Get destination coordinates
                  let destLat: number | undefined;
                  let destLng: number | undefined;
                  if (route.routeType === 'landmark' && route.landmarks?.length > 0) {
                    const destLandmark = route.landmarks.find((l: any) => l.landmark_type === 'destination') || route.landmarks[route.landmarks.length - 1];
                    destLat = destLandmark?.latitude;
                    destLng = destLandmark?.longitude;
                  } else if (route.destination_lat && route.destination_lng) {
                    destLat = route.destination_lat;
                    destLng = route.destination_lng;
                  }
                  
                  return (
                    <Box
                      key={route.id}
                      sx={{
                        backgroundColor: darkMode ? '#1e293b' : '#ffffff',
                        border: darkMode ? '1px solid rgba(255,255,255,0.06)' : '1px solid rgba(0,0,0,0.06)',
                        borderRadius: '12px',
                        boxShadow: '0 6px 16px rgba(34, 197, 94, 0.25), 0 4px 12px rgba(0,0,0,0.12)',
                        px: 1.5,
                        py: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 0.5,
                        transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                        '&:hover': {
                          transform: 'scale(1.02)',
                          boxShadow: '0 12px 28px rgba(34, 197, 94, 0.40), 0 8px 20px rgba(0,0,0,0.18)',
                        }
                      }}
                    >
                      {/* ROW 1: Icon + Route Name */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
                        <Box sx={{
                          width: 32,
                          height: 32,
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          bgcolor: 'rgba(34, 197, 94, 0.2)',
                          flexShrink: 0
                        }}>
                          <RouteIcon sx={{ color: '#22c55e', fontSize: 18 }} />
                        </Box>
                        <Typography 
                          variant="subtitle2" 
                          sx={{ 
                            fontWeight: 700, 
                            color: cyber.textPrimary,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            fontSize: '1.05rem',
                            lineHeight: 1.25,
                            flex: 1
                          }}
                        >
                          {route.name}
                        </Typography>
                      </Box>

                      {/* ROW 2: Chips - Type, Destination, Waypoints, Date */}
                      <Box sx={{ 
                        display: 'flex', 
                        gap: 0.75, 
                        flexWrap: 'wrap', 
                        alignItems: 'center',
                        overflow: 'hidden',
                        pb: 0.5,
                      }}>
                        {/* Route Type badge */}
                        {route.routeType === 'landmark' && (
                          <Chip 
                            label="🛤️ Landmark" 
                            size="small" 
                            sx={{ 
                              height: 22, 
                              fontSize: '0.75rem',
                              fontWeight: 600,
                              fontFamily: '"Google Sans Flex", system-ui, sans-serif',
                              backgroundColor: 'rgba(34, 197, 94, 0.15)',
                              color: '#22c55e'
                            }} 
                          />
                        )}
                        
                        {/* Destination */}
                        {route.destination_name && (
                          <Chip
                            label={`🏁 ${route.destination_name}`}
                            size="small"
                            sx={{
                              height: 22,
                              fontSize: '0.75rem',
                              fontWeight: 600,
                              fontFamily: '"Google Sans Flex", system-ui, sans-serif',
                              backgroundColor: darkMode ? 'rgba(96, 165, 250, 0.15)' : 'rgba(30, 64, 175, 0.1)',
                              color: cyber.neonBlue || '#60a5fa',
                              maxWidth: 160,
                              '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis' }
                            }}
                          />
                        )}

                        {/* Waypoint count */}
                        <Chip 
                          label={`📍 ${route.actual_waypoint_count || route.waypoint_count || 0} ${route.routeType === 'landmark' ? 'landmarks' : 'waypoints'}`}
                          size="small" 
                          sx={{ 
                            height: 22,
                            fontSize: '0.75rem',
                            fontWeight: 600,
                            fontFamily: '"Google Sans Flex", system-ui, sans-serif',
                            backgroundColor: darkMode ? 'rgba(251, 191, 36, 0.15)' : 'rgba(180, 83, 9, 0.1)',
                            color: darkMode ? '#fbbf24' : '#b45309'
                          }}
                        />

                        {/* Distance for landmark routes */}
                        {route.routeType === 'landmark' && route.total_distance_meters && (
                          <Chip 
                            label={`📏 ${(route.total_distance_meters / 1609.34).toFixed(1)} mi`}
                            size="small" 
                            sx={{ 
                              height: 22,
                              fontSize: '0.75rem',
                              fontWeight: 600,
                              fontFamily: '"Google Sans Flex", system-ui, sans-serif',
                              backgroundColor: darkMode ? 'rgba(52, 211, 153, 0.1)' : 'rgba(6, 95, 70, 0.1)',
                              color: cyber.chipGreen || '#34d399'
                            }}
                          />
                        )}

                        {/* Date */}
                        {route.created_at && (
                          <Chip 
                            label={new Date(route.created_at).toLocaleDateString()}
                            size="small" 
                            sx={{ 
                              height: 22,
                              fontSize: '0.75rem',
                              fontWeight: 600,
                              fontFamily: '"Google Sans Flex", system-ui, sans-serif',
                              backgroundColor: darkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(100, 116, 139, 0.1)',
                              color: darkMode ? '#ffffff' : '#334155'
                            }}
                          />
                        )}

                        {/* Status badges */}
                        {route.status === 'archived' && (
                          <Chip 
                            label="Archived" 
                            size="small" 
                            sx={{ 
                              height: 22, 
                              fontSize: '0.75rem',
                              fontWeight: 600,
                              fontFamily: '"Google Sans Flex", system-ui, sans-serif',
                              backgroundColor: 'rgba(251, 191, 36, 0.2)',
                              color: '#fbbf24'
                            }} 
                          />
                        )}
                        {route.publish_status === 'needs_republish' && (
                          <Chip 
                            label="Needs Republish" 
                            size="small" 
                            sx={{ 
                              height: 22, 
                              fontSize: '0.75rem',
                              fontWeight: 600,
                              fontFamily: '"Google Sans Flex", system-ui, sans-serif',
                              backgroundColor: 'rgba(251, 146, 60, 0.2)',
                              color: '#fb923c'
                            }} 
                          />
                        )}
                        {route.publish_status === 'draft' && !route.routeType && (
                          <Chip 
                            label="Draft" 
                            size="small" 
                            sx={{ 
                              height: 22, 
                              fontSize: '0.75rem',
                              fontWeight: 600,
                              fontFamily: '"Google Sans Flex", system-ui, sans-serif',
                              backgroundColor: 'rgba(148, 163, 184, 0.2)',
                              color: cyber.textMuted
                            }} 
                          />
                        )}
                      </Box>

                      {/* ROW 3: Action Buttons */}
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', gap: 0.5, pt: 0.5 }}>
                        {/* Save/Star */}
                        <Tooltip title={route.userStarred ? "Remove from saved" : "Save route"} arrow>
                          <IconButton
                            size="small"
                            onClick={() => handleStarRoute(route)}
                            sx={{
                              width: 40,
                              height: 40,
                              color: route.userStarred ? '#fbbf24' : cyber.textMuted,
                              backgroundColor: route.userStarred 
                                ? 'rgba(251, 191, 36, 0.2)' 
                                : darkMode ? 'rgba(148, 163, 184, 0.12)' : 'rgba(148, 163, 184, 0.1)',
                              '&:hover': {
                                color: '#fbbf24',
                                backgroundColor: 'rgba(251, 191, 36, 0.3)'
                              }
                            }}
                          >
                            {route.userStarred ? <StarIcon sx={{ fontSize: '1.25rem' }} /> : <StarBorderIcon sx={{ fontSize: '1.25rem' }} />}
                          </IconButton>
                        </Tooltip>

                        {/* View on Map */}
                        <Tooltip title="View on Map" arrow>
                          <IconButton
                            size="small"
                            onClick={() => {
                              telegram.hapticFeedback('light');
                              setSelectedRoute(route);
                            }}
                            sx={{
                              width: 40,
                              height: 40,
                              color: cyber.neonBlue || '#60a5fa',
                              backgroundColor: darkMode ? 'rgba(96, 165, 250, 0.12)' : 'rgba(30, 64, 175, 0.1)',
                              '&:hover': { backgroundColor: darkMode ? 'rgba(96, 165, 250, 0.25)' : 'rgba(30, 64, 175, 0.2)' }
                            }}
                          >
                            <MapIcon sx={{ fontSize: '1.25rem' }} />
                          </IconButton>
                        </Tooltip>

                        {/* Share */}
                        <Tooltip title="Share" arrow>
                          <IconButton
                            size="small"
                            onClick={() => {
                              telegram.hapticFeedback('light');
                              const mapsUrl = destLat && destLng 
                                ? `https://www.google.com/maps/search/?api=1&query=${destLat},${destLng}`
                                : window.location.origin;
                              const shareText = `🛣️ Caliche Route: ${route.name}\n${route.description || ''}\n\n📍 ${mapsUrl}\n\n📱 Find more: @ziawellnavbot`;
                              telegram.webApp.openTelegramLink(`https://t.me/share/url?url=${encodeURIComponent(mapsUrl)}&text=${encodeURIComponent(shareText)}`);
                            }}
                            sx={{
                              width: 40,
                              height: 40,
                              color: darkMode ? '#22c55e' : '#16a34a',
                              backgroundColor: darkMode ? 'rgba(34, 197, 94, 0.12)' : 'rgba(34, 197, 94, 0.1)',
                              '&:hover': { backgroundColor: darkMode ? 'rgba(34, 197, 94, 0.22)' : 'rgba(34, 197, 94, 0.18)' }
                            }}
                          >
                            <ShareIcon sx={{ fontSize: '1.25rem' }} />
                          </IconButton>
                        </Tooltip>

                        {/* In-App Navigation */}
                        <Tooltip title="Navigate" arrow>
                          <IconButton
                            size="small"
                            onClick={() => {
                              telegram.hapticFeedback('light');
                              setSelectedRoute(route);
                              setToast({ open: true, message: '🧭 View route on map for navigation', severity: 'info' });
                            }}
                            sx={{
                              width: 40,
                              height: 40,
                              color: darkMode ? '#22d3ee' : '#0891b2',
                              backgroundColor: darkMode ? 'rgba(34, 211, 238, 0.15)' : 'rgba(8, 145, 178, 0.1)',
                              '&:hover': { backgroundColor: darkMode ? 'rgba(34, 211, 238, 0.3)' : 'rgba(8, 145, 178, 0.2)' }
                            }}
                          >
                            <ZiaCompassIcon sx={{ fontSize: '1.25rem' }} />
                          </IconButton>
                        </Tooltip>

                        {/* Google Maps */}
                        <Tooltip title="Google Maps" arrow>
                          <IconButton
                            size="small"
                            onClick={() => {
                              telegram.hapticFeedback('light');
                              if (destLat && destLng) {
                                window.open(`https://www.google.com/maps/dir/?api=1&destination=${destLat},${destLng}&travelmode=driving`, '_blank');
                              } else {
                                setToast({ open: true, message: 'No destination coordinates available', severity: 'info' });
                              }
                            }}
                            sx={{
                              width: 40,
                              height: 40,
                              color: '#4285F4',
                              backgroundColor: darkMode ? 'rgba(66, 133, 244, 0.15)' : 'rgba(66, 133, 244, 0.1)',
                              '&:hover': { backgroundColor: darkMode ? 'rgba(66, 133, 244, 0.3)' : 'rgba(66, 133, 244, 0.2)' }
                            }}
                          >
                            <GoogleIcon sx={{ fontSize: '1.25rem' }} />
                          </IconButton>
                        </Tooltip>

                        {routesTab === 'my' && (
                          <>
                            {/* Publish button */}
                            {route.publish_status === 'draft' && (
                              <Tooltip title="Publish to Community" arrow>
                                <IconButton 
                                  size="small"
                                  onClick={() => handlePublishRoute(route, 'publish')}
                                  sx={{
                                    width: 40,
                                    height: 40,
                                    color: '#22c55e',
                                    backgroundColor: darkMode ? 'rgba(34, 197, 94, 0.12)' : 'rgba(34, 197, 94, 0.1)',
                                    '&:hover': { backgroundColor: darkMode ? 'rgba(34, 197, 94, 0.25)' : 'rgba(34, 197, 94, 0.2)' }
                                  }}
                                >
                                  <PublishIcon sx={{ fontSize: '1.25rem' }} />
                                </IconButton>
                              </Tooltip>
                            )}
                            {/* Republish button */}
                            {route.routeType !== 'landmark' && route.publish_status === 'needs_republish' && (
                              <Tooltip title="Republish Updates" arrow>
                                <IconButton 
                                  size="small"
                                  onClick={() => handlePublishRoute(route, 'republish')}
                                  sx={{
                                    width: 40,
                                    height: 40,
                                    color: '#fb923c',
                                    backgroundColor: darkMode ? 'rgba(251, 146, 60, 0.12)' : 'rgba(251, 146, 60, 0.1)',
                                    '&:hover': { backgroundColor: darkMode ? 'rgba(251, 146, 60, 0.25)' : 'rgba(251, 146, 60, 0.2)' }
                                  }}
                                >
                                  <PublishIcon sx={{ fontSize: '1.25rem' }} />
                                </IconButton>
                              </Tooltip>
                            )}
                            {/* Unpublish button */}
                            {route.publish_status === 'published' && route.is_public && (
                              <Tooltip title="Unpublish" arrow>
                                <IconButton 
                                  size="small"
                                  onClick={() => handlePublishRoute(route, 'unpublish')}
                                  sx={{
                                    width: 40,
                                    height: 40,
                                    color: cyber.textMuted,
                                    backgroundColor: darkMode ? 'rgba(148, 163, 184, 0.12)' : 'rgba(148, 163, 184, 0.1)',
                                    '&:hover': { backgroundColor: darkMode ? 'rgba(148, 163, 184, 0.25)' : 'rgba(148, 163, 184, 0.2)' }
                                  }}
                                >
                                  <VisibilityOffIcon sx={{ fontSize: '1.25rem' }} />
                                </IconButton>
                              </Tooltip>
                            )}
                            {/* Archive button */}
                            {route.routeType !== 'landmark' && route.status === 'active' && (
                              <Tooltip title="Archive Route" arrow>
                                <IconButton 
                                  size="small"
                                  onClick={() => handleArchiveRoute(route)}
                                  sx={{
                                    width: 40,
                                    height: 40,
                                    color: cyber.textMuted,
                                    backgroundColor: darkMode ? 'rgba(148, 163, 184, 0.12)' : 'rgba(148, 163, 184, 0.1)',
                                    '&:hover': { backgroundColor: darkMode ? 'rgba(148, 163, 184, 0.25)' : 'rgba(148, 163, 184, 0.2)' }
                                  }}
                                >
                                  <ArchiveIcon sx={{ fontSize: '1.25rem' }} />
                                </IconButton>
                              </Tooltip>
                            )}
                          </>
                        )}

                        {routesTab !== 'my' && route.routeType !== 'landmark' && (
                          <Tooltip title="Fork this route" arrow>
                            <IconButton 
                              size="small"
                              onClick={() => handleForkRoute(route)}
                              sx={{
                                width: 40,
                                height: 40,
                                color: '#a855f7',
                                backgroundColor: darkMode ? 'rgba(168, 85, 247, 0.12)' : 'rgba(168, 85, 247, 0.1)',
                                '&:hover': { backgroundColor: darkMode ? 'rgba(168, 85, 247, 0.25)' : 'rgba(168, 85, 247, 0.2)' }
                              }}
                            >
                              <ForkRightIcon sx={{ fontSize: '1.25rem' }} />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            )}
          </Box>
        )}
      </DialogContent>

      {/* Toast */}
      <Snackbar
        open={toast.open}
        autoHideDuration={4000}
        onClose={() => setToast({ ...toast, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity={toast.severity} onClose={() => setToast({ ...toast, open: false })}>
          {toast.message}
        </Alert>
      </Snackbar>

      {/* Native Camera Dialog (for Android) */}
      <Dialog
        open={showNativeCamera}
        fullScreen
        PaperProps={{
          sx: {
            backgroundColor: '#000',
            overflow: 'hidden'
          }
        }}
      >
        <Box sx={{ 
          position: 'relative', 
          width: '100%', 
          height: '100%',
          display: 'flex',
          flexDirection: 'column'
        }}>
          {/* Camera preview */}
          <Box sx={{ 
            flex: 1, 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            overflow: 'hidden',
            position: 'relative'
          }}>
            {/* Loading indicator */}
            {!cameraReady && !cameraError && (
              <Box sx={{
                position: 'absolute',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 10
              }}>
                <CyberSpinner size={50} />
              </Box>
            )}
            
            {/* Error message */}
            {cameraError && (
              <Box sx={{
                position: 'absolute',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 2,
                zIndex: 10,
                p: 3,
                textAlign: 'center'
              }}>
                <Typography sx={{ color: '#ff6b6b' }}>⚠️ {cameraError}</Typography>
                <Button 
                  variant="contained" 
                  onClick={() => {
                    stopNativeCamera();
                    galleryInputRef.current?.click();
                  }}
                  sx={{ mt: 2 }}
                >
                  Choose from Gallery Instead
                </Button>
              </Box>
            )}
            
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                opacity: cameraReady ? 1 : 0
              }}
            />
          </Box>
          
          {/* Camera controls */}
          <Box sx={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            py: 3,
            px: 2,
            display: 'flex',
            justifyContent: 'space-around',
            alignItems: 'center',
            background: 'linear-gradient(transparent, rgba(0,0,0,0.8))'
          }}>
            {/* Close button */}
            <IconButton
              onClick={stopNativeCamera}
              sx={{
                width: 50,
                height: 50,
                backgroundColor: 'rgba(255,255,255,0.2)',
                '&:hover': { backgroundColor: 'rgba(255,255,255,0.3)' }
              }}
            >
              <CloseIcon sx={{ color: 'white', fontSize: 28 }} />
            </IconButton>
            
            {/* Capture button */}
            <IconButton
              onClick={captureFromNativeCamera}
              disabled={!cameraReady}
              sx={{
                width: 80,
                height: 80,
                backgroundColor: cameraReady ? 'white' : 'rgba(255,255,255,0.3)',
                border: '4px solid rgba(255,255,255,0.5)',
                '&:hover': { backgroundColor: cameraReady ? '#f0f0f0' : 'rgba(255,255,255,0.3)' },
                '&:active': { transform: cameraReady ? 'scale(0.95)' : 'none' }
              }}
            >
              <CameraAltIcon sx={{ color: cameraReady ? '#333' : '#666', fontSize: 40 }} />
            </IconButton>
            
            {/* Switch camera button */}
            <IconButton
              onClick={switchCamera}
              disabled={!cameraReady}
              sx={{
                width: 50,
                height: 50,
                backgroundColor: 'rgba(255,255,255,0.2)',
                '&:hover': { backgroundColor: 'rgba(255,255,255,0.3)' }
              }}
            >
              <FlipCameraAndroidIcon sx={{ color: 'white', fontSize: 28 }} />
            </IconButton>
          </Box>
        </Box>
        
        {/* Hidden canvas for capturing */}
        <canvas ref={canvasRef} style={{ display: 'none' }} />
      </Dialog>

      {/* Share Dialog - appears after photo submission */}
      <Dialog 
        open={showShareDialog} 
        onClose={closeShareDialog}
        PaperProps={{
          sx: {
            background: cyber.bgCard,
            border: `2px solid ${cyber.neonGreen}`,
            borderRadius: 3,
            minWidth: { xs: '90%', sm: 360 }
          }
        }}
      >
        <DialogContent sx={{ p: 3 }}>
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <Box sx={{ 
              width: 60, 
              height: 60, 
              borderRadius: '50%', 
              background: `${cyber.neonGreen}30`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mx: 'auto',
              mb: 2
            }}>
              <Typography sx={{ fontSize: 32 }}>✅</Typography>
            </Box>
            <Typography variant="h6" sx={{ color: cyber.textPrimary, fontWeight: 600 }}>
              Photo Submitted!
            </Typography>
            <Typography sx={{ color: cyber.textMuted, mt: 1 }}>
              Your photo is pending review. Share the location with your crew?
            </Typography>
          </Box>

          {lastSubmittedLocation && (
            <Box sx={{ 
              background: cyber.bgSecondary, 
              borderRadius: 2, 
              p: 2, 
              mb: 3,
              border: `1px solid ${cyber.borderDefault}`
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Typography sx={{ fontSize: 20 }}>
                  {LANDMARK_TYPES.find(l => l.value === lastSubmittedLocation.landmarkType)?.icon || '📍'}
                </Typography>
                <Typography sx={{ color: cyber.textPrimary, fontWeight: 500 }}>
                  {LANDMARK_TYPES.find(l => l.value === lastSubmittedLocation.landmarkType)?.label || 'Location'}
                </Typography>
              </Box>
              {lastSubmittedLocation.description && (
                <Typography sx={{ color: cyber.textMuted, fontSize: '0.9rem' }}>
                  {lastSubmittedLocation.description}
                </Typography>
              )}
              <Chip
                label={`📍 ${lastSubmittedLocation.lat.toFixed(5)}, ${lastSubmittedLocation.lng.toFixed(5)}`}
                size="small"
                onClick={() => window.open(`https://www.google.com/maps/search/?api=1&query=${lastSubmittedLocation.lat},${lastSubmittedLocation.lng}`, '_blank')}
                sx={{
                  mt: 1,
                  backgroundColor: 'rgba(251, 191, 36, 0.15)',
                  color: '#fbbf24',
                  cursor: 'pointer',
                  fontWeight: 600,
                  fontSize: '0.75rem',
                  '&:hover': { backgroundColor: 'rgba(251, 191, 36, 0.3)' }
                }}
              />
            </Box>
          )}

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Button
              variant="contained"
              startIcon={<ShareIcon />}
              onClick={handleShare}
              fullWidth
              sx={{ 
                background: `linear-gradient(135deg, ${cyber.neonBlue} 0%, ${cyber.neonCyan} 100%)`,
                color: '#fff',
                fontWeight: 600,
                py: 1.5,
                '&:hover': {
                  background: `linear-gradient(135deg, ${cyber.neonCyan} 0%, ${cyber.neonBlue} 100%)`,
                }
              }}
            >
              Share Location
            </Button>
            <Button
              variant="outlined"
              startIcon={<ContentCopyIcon />}
              onClick={handleCopyLink}
              fullWidth
              sx={{ 
                borderColor: cyber.borderDefault, 
                color: cyber.textPrimary,
                '&:hover': {
                  borderColor: cyber.neonBlue,
                  background: `${cyber.neonBlue}10`
                }
              }}
            >
              Copy Link
            </Button>
            <Button 
              onClick={closeShareDialog}
              fullWidth
              sx={{ color: cyber.textMuted }}
            >
              Done
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
}
